// ===================================================
// src/components/Planning/Planning.js - FINAL VERSION (Delete + Forecast + Stock Graphs)
// ===================================================
import React, { useState, useEffect } from 'react';
import { RefreshCw, Plus, Home, Download, Upload } from 'lucide-react';
import { usePlanningData } from './hooks/usePlanningData';
import PlanningStats from './PlanningStats';
import PlanningTable from './PlanningTable';
import EditDayModal from './EditDayModal';
import AddDayModal from './AddDayModal';
import GeneratePlanModal from './GeneratePlanModal';
import StockShipmentTimeline from './StockShipmentTimeline';
import { FlaskConical } from 'lucide-react';
import SimulationBoard from './SimulationBoard';
import UploadSalesTargetsModal from './UploadSalesTargetsModal';
import UploadProductionTargetsModal from './UploadProductionTargetsModal';

const Planning = () => {
  // 🧠 Custom hook for data management
  const {
    allData,
    loading,
    error,
    stats,
    fetchAll,
    fetchDayDetails,
    handleAddDay,
    handleUpdateDay,
    handleDeleteDay,
    handleExportData
  } = usePlanningData();

  // 🎛️ Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalLoading, setAddModalLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [selectedDayForEdit, setSelectedDayForEdit] = useState(null);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadProductionModalOpen, setUploadProductionModalOpen] = useState(false);

  // 🧩 Manual Stock Overrides (shared between table & simulator)
  const [manualStockOverrides, setManualStockOverrides] = useState({});

  useEffect(() => {
  const loadOverrides = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`);
      const text = await res.text();

      const data = JSON.parse(text);
      if (data && typeof data === "object") {
        setManualStockOverrides(data);
      } else {
        console.warn("⚠️ Invalid data structure for manualStockOverrides, resetting to {}");
        setManualStockOverrides({});
      }
    } catch (err) {
      console.error("❌ Failed to fetch manualStockOverrides:", err);
      setManualStockOverrides({});
    }
  };

  loadOverrides();
}, []);

  // ➕ Add new day
  const onAddDay = async (formData) => {
    setAddModalLoading(true);
    try {
      await handleAddDay(formData);
      setAddModalOpen(false);
      await fetchAll();
    } catch (error) {
      console.error('❌ Failed to add planning day:', error);
      alert(`Failed to add planning day: ${error.message}`);
    } finally {
      setAddModalLoading(false);
    }
  };

  // ✏️ Edit day
  const onEditDay = async (day) => {
    try {
      console.log('🔧 Editing day:', day.date);
      const detailedDayData = await fetchDayDetails(day.date);
      setSelectedDayForEdit(detailedDayData);
      setEditModalOpen(true);
    } catch (error) {
      console.error('❌ Error loading day details:', error);
      alert(`Failed to load day details: ${error.message}`);
    }
  };

  // 💾 Update day
  const onUpdateDay = async (formData) => {
    setEditModalLoading(true);
    try {
      console.log('🔄 Updating planning day:', formData);
      await handleUpdateDay(formData);
      setEditModalOpen(false);
      setSelectedDayForEdit(null);
      await fetchAll();
    } catch (error) {
      console.error('❌ Failed to update planning day:', error);
      alert(`Failed to update planning day: ${error.message}`);
    } finally {
      setEditModalLoading(false);
    }
  };

  // 🗑️ Delete day
  const onDeleteDay = async (day) => {
    try {
      console.log('🗑️ Deleting day:', day.date);
      await handleDeleteDay(day.date);
      await fetchAll();
    } catch (error) {
      console.error('❌ Failed to delete planning day:', error);
      alert(`Failed to delete planning day: ${error.message}`);
    }
  };

  // 🧮 Generate forecast
  const handleGeneratePlan = async (forecastData) => {
    try {
      const res = await fetch('/api/plan/forecast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: forecastData })
      });

      if (!res.ok) throw new Error('Failed to create forecast plan');
      alert('✅ Forecast plan saved successfully');
      await fetchAll();
    } catch (err) {
      console.error('Error generating forecast:', err);
      alert('❌ Failed to save forecast');
    }
  };

  // 🔒 Modal close
  const onEditModalClose = () => {
    setEditModalOpen(false);
    setSelectedDayForEdit(null);
  };
  const onAddModalClose = () => setAddModalOpen(false);

  // 🎨 Common button style
  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: '500',
    textDecoration: 'none',
    transition: 'all 0.2s',
    minHeight: '36px'
  };

  return (
    <div style={{ padding: 0, minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* ===== Header ===== */}
      <div
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1400px',
            margin: '0 auto'
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}
          >
            📊 Planning Dashboard
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <a
              href="/dashboard"
              style={{
                ...buttonStyle,
                backgroundColor: '#f3f4f6',
                color: '#4b5563'
              }}
            >
              <Home size={18} /> Home
            </a>
              
              <button
              onClick={() => setUploadModalOpen(true)}
              style={{
                ...buttonStyle,
                backgroundColor: '#f59e0b',
                color: 'white'
              }}
              disabled={loading}
            >
              <Upload size={18} /> Upload Targets
            </button>

            <button
            onClick={() => setUploadProductionModalOpen(true)}
            style={{
              ...buttonStyle,
              backgroundColor: '#f97316',
              color: 'white'
            }}
            disabled={loading}
          >
            <Upload size={18} /> Upload Production
          </button>


            <button
              onClick={handleExportData}
              style={{
                ...buttonStyle,
                backgroundColor: '#10b981',
                color: 'white'
              }}
              disabled={loading || allData.length === 0}
            >
              <Download size={18} /> Export Excel
            </button>

              <button
              onClick={() => setSimOpen(true)}
              style={{
                ...buttonStyle,
                backgroundColor: '#0ea5e9',
                color: 'white'
              }}
              disabled={loading || allData.length === 0}
            >
              <FlaskConical size={18} /> Simulation
            </button>


            <button
              onClick={() => setPlanModalOpen(true)}
              style={{
                ...buttonStyle,
                backgroundColor: '#8b5cf6',
                color: 'white'
              }}
              disabled={loading}
            >
              📅 Define Plan
            </button>

            <button
              onClick={fetchAll}
              style={{
                ...buttonStyle,
                backgroundColor: '#6366f1',
                color: 'white'
              }}
              disabled={loading}
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

        {/* ===== Main Content ===== */}
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}
        >
          <PlanningStats stats={stats} allData={filteredData} loading={loading} />
          <PlanningTable
            allData={allData}
            loading={loading}
            error={error}
            onEditDay={onEditDay}
            stats={stats}
            onDeleteDay={onDeleteDay}
            onAddFirstDay={() => setAddModalOpen(true)}
            onFilteredData={(data) => setFilteredData(data)}
            manualStockOverrides={manualStockOverrides}
            setManualStockOverrides={setManualStockOverrides}
          />

          <StockShipmentTimeline data={allData} region="US" />
          <StockShipmentTimeline data={allData} region="EU" />
        </div>

        {/* 🧪 Simulation overlay should be OUTSIDE main content */}
          <SimulationBoard
            open={simOpen}
            onClose={() => setSimOpen(false)}
            data={allData}
            onApplyToDb={async (days) => {
              try {
                const res = await fetch('/api/plan/forecast', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ days })
                });
                if (!res.ok) throw new Error('Failed to save forecast');
                alert('✅ Forecast saved successfully');
                await fetchAll();
              } catch (err) {
                console.error('❌ Failed to save forecast:', err);
                alert('❌ Failed to save forecast');
              }
            }}
            manualStockOverrides={manualStockOverrides}          // ✅ new
            setManualStockOverrides={setManualStockOverrides}    // ✅ new
          />


      {/* ===== Modals ===== */}
      <AddDayModal
        isOpen={addModalOpen}
        onClose={onAddModalClose}
        onSave={onAddDay}
        loading={addModalLoading}
      />

        <EditDayModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdateDay}
          dayData={selectedDayForEdit}
          loading={loading}
        />

        {uploadModalOpen && (
          <UploadSalesTargetsModal onClose={() => setUploadModalOpen(false)} />
        )}

        {uploadProductionModalOpen && (
          <UploadProductionTargetsModal onClose={() => setUploadProductionModalOpen(false)} />
        )}

      <GeneratePlanModal
        isOpen={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        onGenerate={handleGeneratePlan}
      />


      {/* ===== CSS ===== */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        table { font-variant-numeric: tabular-nums; }
        button:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        button:active:not(:disabled) { transform: translateY(0); }
        .table-container::-webkit-scrollbar { height: 8px; }
        .table-container::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
        .table-container::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .table-container::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
};

export default Planning;
